/**
 * Service Dependencies
 */
import qs from 'qs'
import http from './HttpService'
import { cloneDeep } from 'lodash'

const stripCapex = data => {
    let obj = cloneDeep(data)
    delete obj.capex.AcquisitionCostSerializer
    delete obj.capex.HardCostSerializer
    delete obj.capex.SoftCostSerializer
    return obj
}

/**
 * Retrieve the user's projects
 *
 * @return {Promise}
 */
export const fetchProjects = (params = {}) => {
    return http.get(`projects/?${qs.stringify(params)}`)
}

/**
 * Retrieve a single project for a given user
 *
 * @type {Promise}
 */
export const fetchSingleProject = (projectId = null, params = {}) => {
    return http.get(`projects/${projectId}/`, params)
}

/**
 * Generate a key to download an Excel document
 *
 * @param  {Number} projectId
 * @return {Promise}
 */
export const generateDownloadKey = (projectId = null) => {
    return http.post(`projects/${projectId}/export-to-excel/`)
}

/**
 * Share project request
 *
 * @param  {String} email
 * @param  {Number} level
 * @param  {Number} id
 * @return {Promise}
 */
export const shareProjectWithUser = (email, level, id) => {
    return http.post('projects/collaborate', {
        invitee_email: email,
        privilege_level: level,
        project_id: id
    })
}

/**
 * Request a global list of project links generated
 *
 * @return {Promise}
 */
export const getProjectLinks = () => {
    return http.get('projects/link-invites/')
}

/**
 * Update the project link active status
 *
 * @param  {Number} id
 * @param  {Object} payload
 * @return {Promise}
 */
export const updateProjectLink = (id, status) => {
    return http.patch(`projects/link-invites/${id}/`, {
        active: status
    })
}

/**
 * Delete existing project link
 *
 * @param  {Number} id
 * @return {Promise}
 */
export const deleteProjectLink = (id) => {
    return http.del(`projects/link-invites/${id}/`)
}

/**
 * Generate a new project link
 *
 * @param  {Number} id
 * @return {Promise}
 */
export const createProjectLink = (id) => {
    return http.post('projects/link-invites/', {
        project_id: id
    })
}

/**
 * Return a humanized string from a serializer key
 *
 * @param  {String} key
 * @return {String}
 */
export const humanizeCapexLabel = (key) => {
    switch (key) {
        case 'OneTimeReserveSerializer':
            return 'One Time Costs'
        case 'HardCostSerializer':
            return 'Hard Costs'
        case 'SoftCostSerializer':
            return 'Soft Costs'
        case 'AcquisitionCostSerializer':
            return 'Acquisition Costs'
        default:
            return key
    }
}

/**
 * Return an existing project model from the API
 *
 * @param  {Number} id
 * @return {Promise}
 */
export const getProject = (id) => {
    return http.get(`projects/${id}/?edit=true`)
}

/**
 * Save a project with given data
 *
 * @return {Promise}
 */
export const saveProject = (data) => {
    return http.post('projects/create/', stripCapex(data))
}

/**
 * Attempt to update the project via the API
 *
 * @param  {Number} id
 * @param  {Object} data
 * @return {Promise}
 */
export const updateProject = (id, data) => {
    return http.put(`projects/${id}/`, stripCapex(data))
}

/**
 * Fetch the available tags
 *
 * @return {Promise}
 */
export const fetchTags = () => {
    return http.get('information/tags')
}

/**
 * Fetch the project card data
 *
 * @return {Promise}
 */
export const getProjectCardData = (id) => {
    return http.get(`projects/${id}/?card=true`)
}

/**
 * Remove a link
 *
 * @param  {Number} id
 * @return {Promise}
 */
export const removeLink = (id) => {
    return http.del(`projects/link-invites/${id}/`)
}

/**
 * Check for a valid PIN and return the associated project if the challenge passes.
 *
 * @param  {Number} pin
 * @return {Promise}
 */
export const checkPin = (key, payload) => {
    return http.post(`auth/${key}/`, payload)
}

/**
 * Check for a valid shared URL key
 *
 * @param  {String} key
 * @return {Promise}
 */
export const checkSharedProjectKey = (key) => {
    return http.get(`auth/${key}/`)
}

/**
 * Get the suggested project name when copying a project
 *
 * @param  {Number} id
 * @return {Promise}
 */
export const getSuggestedCopiedProjectName = (id) => {
    return http.get('projects/' + id + '/copy/')
}

/**
 * Create a copy of a project
 *
 * @param  {Number} id
 * @param  {String} name
 * @return {Promise}
 */
export const createProjectCopy = (id, name) => {
    return http.post(`projects/${id}/copy/`, {
        name: name,
        is_template: false
    })
}

/**
 * Attempt to create a template from a given project ID
 *
 * @param  {Number} id
 * @param  {String} name
 * @return {Promise}
 */
export const createTemplateFromProject = (payload) => {
    return http.post('projects/templates/', payload)
}

/**
 * Query the API for the available template list
 *
 * @return {Promise}
 */
export const getProjectTemplates = () => {
    return http.get('projects/templates/')
}

/**
 * Retrieve a template by ID
 *
 * @param  {Number} id
 * @return {Promise}
 */
export const getProjectFromTemplate = (id) => {
    return http.get(`projects/templates/${id}`)
}

/**
 * Get the user context for a given project ID
 *
 * @param  {Number} id
 * @return {Promise}
 */
export const getUserDataForProject = (id) => {
    return http.get(`projects/${id}/privileges/`)
}

/**
 * Call the API and update a user's level on a project
 *
 * @param  {Number} projectId
 * @param  {Number} userId
 * @param  {Number} level
 * @return {Promise}
 */
export const updatePrivilege = (projectId, privilegeId, level) => {
    return http.patch(`projects/${projectId}/privileges/${privilegeId}/`, {
        privilege_level: level
    })
}

/**
 * Revoke a privilege on a given project
 *
 * @param  {Number} projectId
 * @param  {Number} privilegeid
 * @return {Promise}
 */
export const revokePrivilege = (projectId, privilegeId) => {
    return http.del(`projects/${projectId}/privileges/${privilegeId}/`)
}

/**
 * Request the API to generate a new privilege for a given project
 *
 * @param  {Number} projectId
 * @return {Promise}
 */
export const createPrivilege = (projectId, data) => {
    return http.post(`projects/${projectId}/privileges/`, data)
}

/**
 * Destroy an invitation
 *
 * @param  {Number} invitationId
 * @return {Promise}
 */
export const revokeInvite = (invitationId) => {
    return http.del(`projects/collaborate/${invitationId}`)
}

/**
 * Update an invite level
 *
 * @param  {Number} invitationId
 * @param  {Number} level
 * @return {Promise}
 */
export const updateInvite = (invitationId, level) => {
    return http.patch(`projects/collaborate/${invitationId}`, {
        privilege_level: level
    })
}



// WEBPACK FOOTER //
// ./src/services/ProjectService.js